import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { ChartBarIcon, PlayIcon, TrashIcon } from '@heroicons/react/24/outline'
import { QueryResults } from './QueryResults'
import { StandardLoader } from '@/components/StandardLoader'
import { GenerateDataChart } from './GenerateDataChart'
import { useQueryRunner } from './useQueryRunner'
import { QueryResult } from '@/components/DataWorkspace/types'
import { apis } from '@modules/apiClients'
import {
  currentQueryAtom,
  isSavingWorkAtom,
  lastSaveWorkDateAtom,
} from '@/store/saveWorkAtoms'
import { useAtomValue, useSetAtom } from 'jotai'
import { useTar } from '@/modules/i18n'
import { workspaceLayoutAtom } from '@/store/workspaceAtoms'
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  ResizablePanelGroup,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@duck/ui'
import { getTableDataFromQuery, persistPanesLayout, WorkspaceLayouts } from './utils'
import { DatabaseSchema } from '@/components/DataWorkspace/DatabaseSchema'
import { WorkspacePane } from '../Workspace/WorkspacePane'
import { QueryHistory } from '../QueryHistory/QueryHistory'
import { clearQueryHistoryFromStorage } from '../QueryHistory/queryHistoryStorage'
import { queryHistoryAtom } from '../QueryHistory/historyAtoms'
import { useEventListener } from '@/modules/EventBusModule'
import { useIsMobile } from '@duck/ui'
import { cn } from '@duck/ui'
import { WorkspaceResizeHandle } from '../Workspace/WorkspaceResizeHandle'
import { debounce } from 'lodash'
import { loadWorkspaceStatesFromStorage } from '@/store/workspaceAtoms'

const CodeInput = lazy(() =>
  import('./CodeInput').then((module) => ({ default: module.CodeInput }))
)

interface Props {
  challengeId: string;
  tableQueries?: string[];
}

export const DataWorkspace = ({ challengeId, tableQueries }: Props) => {
  const { t } = useTar()
  const isMobile = useIsMobile()
  const workspaceLayout = useAtomValue(workspaceLayoutAtom)
  const [queryResults, setQueryResults] = useState<QueryResult | null>(null)
  const [isVisualizeDialogOpen, setIsVisualizeDialogOpen] = useState(false)
  const [subQuery, setSubQuery] = useState<string | null>(null)
  const queryThrottleRef = useRef<NodeJS.Timeout | null>(null)
  const setIsSaving = useSetAtom(isSavingWorkAtom)
  const setLastSaveDate = useSetAtom(lastSaveWorkDateAtom)
  const setCurrentQuery = useSetAtom(currentQueryAtom)
  const currentQuery = useAtomValue(currentQueryAtom)
  const setQueryHistory = useSetAtom(queryHistoryAtom)

  const onResultsChange = useCallback((result: QueryResult) => {
    setQueryResults(result)
  }, [])

  const { runQuery, isRunning } = useQueryRunner({
    challengeId,
    onResultsChange
  })

  const tables = useMemo(() => {
    return tableQueries?.map((query) => {
      const { name } = getTableDataFromQuery(query)
      return name
    }) || []
  }, [tableQueries])

  const tableColumns = useMemo(() => {
    return tableQueries?.flatMap((query) => {
      const { fields } = getTableDataFromQuery(query)
      return fields.map((field) => field.name)
    }) || []
  }, [tableQueries])

  const handleRunQuery = useCallback(async (query?: string, skipHistory = false) => {
    const queryToRun = subQuery ?? query ?? currentQuery
    await runQuery(queryToRun, !!subQuery || skipHistory)
  }, [currentQuery, subQuery, runQuery])

  useEventListener(
    'codeInput:cmdEnter',
    useCallback(
      ({ query: eventQuery }) => {
        void handleRunQuery(eventQuery)
      },
      [handleRunQuery]
    ),
    [handleRunQuery]
  )

  useEffect(() => {
    void handleRunQuery(undefined, true)
  }, [])

  const handleQueryChange = useCallback((query: string) => {
    setCurrentQuery(query)
    if (queryThrottleRef.current) {
      clearTimeout(queryThrottleRef.current)
    }
    queryThrottleRef.current = setTimeout(async () => {
      setIsSaving(true)
      try {
        await apis.challengeService.saveWork({
          challengeId,
          workState: {
            lastQuery: query,
          }
        })
        setLastSaveDate(new Date())
      } catch (err) {
        console.error('Failed to save query:', err)
      } finally {
        setIsSaving(false)
      }
    }, 1000)
  }, [challengeId, setCurrentQuery, setIsSaving, setLastSaveDate])

  useEffect(() => {
    return () => {
      if (queryThrottleRef.current) {
        clearTimeout(queryThrottleRef.current)
      }
    }
  }, [])
  const handleLayout = useMemo(
    () => debounce((sizes) => {
      const { schema = false, queryHistory = false, query = false, queryOutput = false } = loadWorkspaceStatesFromStorage()
      const newLayout: Partial<WorkspaceLayouts['data']> = {}
      if (queryHistory) {
        newLayout.queryHistory = sizes[0]
      }
      if (schema) {
        newLayout.schema = sizes[+queryHistory]
      }
      if (query) {
        newLayout.query = sizes[+queryHistory + +schema]
      }
      if (queryOutput) {
        newLayout.queryOutput = sizes[+queryHistory + +schema + +query]
      }
      persistPanesLayout.save('data', newLayout)
    }, 100), []
  )

  return (
    <>
      <ResizablePanelGroup
        direction={isMobile ? 'vertical' : (workspaceLayout === 'split-side' ? 'vertical' : 'horizontal')}
        className="h-full"
        onLayout={handleLayout}
      >
        {!isMobile && (
          <>
            <WorkspacePane
              paneKey="queryHistory"
              layoutName="data"
              title={t('common.history')}
              minSize={20}
              maxSize={85}
              titleActions={
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        onClick={() => {
                          const newQueryHistory =
                            clearQueryHistoryFromStorage(challengeId)
                          setQueryHistory(newQueryHistory)
                        }}
                        size="xs"
                        className="flex items-center py-1 px-2 text-xs text-muted-foreground transition-colors bg-transparent hover:bg-secondary/70 hover:text-foreground"
                        aria-label={t('common.clearQueryHistory')}
                      >
                        {t('common.clearAll')}
                        <TrashIcon className="w-4 h-4 ms-1" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>{t('common.clearQueryHistory')}</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              }
            >
              <QueryHistory
                challengeId={challengeId}
                onQuerySelect={(query) => {
                  setCurrentQuery(query)
                  void handleRunQuery(query, true)
                }}
              />
            </WorkspacePane>

            <WorkspaceResizeHandle enablingPanes={['queryHistory', 'schema']} />

            <WorkspacePane
              paneKey="schema"
              layoutName="data"
              title={t('challenges.schema')}
              minSize={15}
              maxSize={85}
            >
              <div className="p-4">
                <DatabaseSchema tableQueries={tableQueries || []} />
              </div>
            </WorkspacePane>
            <WorkspaceResizeHandle enablingPanes={['schema', 'query']} />
          </>
        )}

        <WorkspacePane
          paneKey="query"
          layoutName="data"
          title={t('challenges.query')}
          minSize={15}
          maxSize={85}
        >
          <div className={cn("h-full flex flex-col min-h-0 border-border border-b", isMobile ? 'flex-1' : '')}>
            <Suspense
              fallback={
                <div className="border border-border pt-2 bg-background h-[140px] flex items-center justify-center">
                  <StandardLoader size="md" />
                </div>
              }
            >
              <CodeInput
                query={currentQuery}
                onQueryChange={handleQueryChange}
                tableColumns={tableColumns}
                tables={tables}
                language={'sql'}
                showLoadingIndicator={isRunning}
                onSelection={(selectedText) => setSubQuery(selectedText)}
              />
            </Suspense>
          </div>
          <div className="flex justify-between p-4">
            {!isMobile && (
              <Button
                onClick={() => {
                  handleRunQuery()
                  setIsVisualizeDialogOpen(true)
                }}
                disabled={!queryResults || queryResults.results.length === 0}
                className="space-x-2"
              >
                <ChartBarIcon className="w-4 h-4" />
                <span>{t('common.visualize')}</span>
              </Button>
            )}
            <Button
              onClick={() => void handleRunQuery()}
              disabled={isRunning}
              className="space-x-2"
            >
              <PlayIcon className="w-4 h-4" />
              <span>{subQuery ? t('common.runCHARS', { chars: subQuery.length }) : t('common.run')}</span>
            </Button>
          </div>
        </WorkspacePane>

        <WorkspaceResizeHandle enablingPanes={['query', 'queryOutput']} />

        <WorkspacePane
          paneKey="queryOutput"
          layoutName="data"
          title={t(
            queryResults?.results?.length
              ? 'challenges.resultsAMOUNT'
              : 'common.results',
            { amount: queryResults?.results?.length || 0 }
          )}
          minSize={20}
          maxSize={85}
          titleActions={
            <Badge
              variant="outline"
            >
              {!queryResults?.isFromCache
                ? `${queryResults?.timing ?? '...'}ms`
                : t('common.cached')}
            </Badge>
          }
        >
          <div className="flex flex-col h-full">
            <QueryResults
              queryResults={queryResults?.results}
              queryError={queryResults?.error}
            />
          </div>
        </WorkspacePane>
      </ResizablePanelGroup>

      {/* Visualize Dialog */}
      {queryResults && (
        <Dialog
          open={isVisualizeDialogOpen}
          onOpenChange={setIsVisualizeDialogOpen}
        >
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{t('common.visualizationChart')}</DialogTitle>
            </DialogHeader>
            <GenerateDataChart
              queryResults={queryResults?.results || []}
              query={currentQuery}
              onSubmit={() => setIsVisualizeDialogOpen(false)}
            />
          </DialogContent>
        </Dialog>
      )}
    </>
  )
}